import React, { useState, useEffect } from 'react';
import {
    Box,
    TextField,
    Grid,
    Alert,
    Divider,
    IconButton,
    Typography,
    Card,
    CardContent,
    Button,
    Stack,
    Avatar,
    Chip,
    FormControlLabel,
    Checkbox
} from '@mui/material';
import {
    Badge as BadgeIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    ArrowBack as ArrowBackIcon,
    Security as SecurityIcon,
    ShieldOutlined as ShieldOutlinedIcon,
    Layers as LayersIcon
} from '@mui/icons-material';
import GenericPage, { useDefaultViewMode } from '../../Commun/components/GenericPage';
import GenericTable from '../../Commun/components/GenericTable';
import { blobticService } from '../../Commun/services/blobticService';
import { blobticMessage } from '../../Commun/components/BlobticMessage';
import { useLanguage } from '../../Commun/contexts/LanguageContext';
import { BlobticDataManager } from '../../Commun/services/blobticDataManager';

export interface ProfilFormData {
    PRF_IDF?: string;
    PRF_UIDF?: string;
    PRF_CODE: string;
    PRF_LIB: string;
    PRF_NIVEAU?: string;
    PRF_ADMIN?: boolean;
    LAST_UPDT?: string;
    USR_LIB?: string;
    USER_IDF?: string | number;
}

const initialForm: ProfilFormData = {
    PRF_CODE: '',
    PRF_LIB: '',
    PRF_NIVEAU: '',
    PRF_ADMIN: false
};

const ProfilPage: React.FC = () => {
    const { t } = useLanguage();

    // View state: 'list' (default) or 'form' (new/edit)
    const [view, setView] = useState<'list' | 'form'>('list');

    // List view mode: 'grid' or 'table' (initialized from user default preference)
    const [viewMode, setViewMode] = useDefaultViewMode();

    // Profiles list
    const [profilesList, setProfilesList] = useState<ProfilFormData[]>([]);
    const [listLoading, setListLoading] = useState(false);

    // Form state
    const [formData, setFormData] = useState<ProfilFormData>(initialForm);
    const [error, setError] = useState<string | null>(null);

    // Load profiles from IndexedDB cache / Server DB
    const loadProfiles = async (forceRefresh: boolean = false) => {
        setListLoading(true);
        try {
            const response = await blobticService.executeZoomRequest<ProfilFormData[]>({
                ZoomKey: 'profil_list',
                Action: {
                    OKey: 'Blobtic_Sys_Profil,Sys_Profil,Select'
                }
            }, !forceRefresh);

            if (response.success && Array.isArray(response.data)) {
                setProfilesList(response.data);
            }
        } catch (err: any) {
            console.error('Error loading profiles', err);
        } finally {
            setListLoading(false);
        }
    };

    useEffect(() => {
        loadProfiles();
        // Reactive sync with IndexedDB / WebSocket mutations
        const unsubscribe = BlobticDataManager.subscribe((obj) => {
            if (obj === 'sys_profil') {
                loadProfiles();
            }
        });
        return () => unsubscribe();
    }, []);

    // ── Navigation & Actions ───────────────────────────────────────────────────

    const openNew = () => {
        setFormData(initialForm);
        setError(null);
        setView('form');
    };

    const openEdit = (profil: ProfilFormData) => {
        setFormData({
            ...profil,
            PRF_ADMIN: profil.PRF_ADMIN === true || String(profil.PRF_ADMIN) === 'true'
        });
        setError(null);
        setView('form');
    };

    const backToList = () => {
        setView('list');
        setFormData(initialForm);
        setError(null);
        loadProfiles();
    };

    const handleDelete = async (profil: ProfilFormData) => {
        if (!profil.PRF_IDF) return;

        const confirmed = await blobticMessage.confirm({
            title: t('common.confirm.title.delete'),
            message: t('page.profiles.confirm_delete', profil.PRF_LIB || profil.PRF_CODE),
            severity: 'warning',
            confirmText: t('common.actions.confirm'),
            cancelText: t('btn.cancel')
        });

        if (!confirmed) return;

        try {
            const res = await blobticService.executeZoomRequest({
                ZoomKey: 'profil_delete',
                Action: {
                    OKey: 'Blobtic_Sys_Profil,Sys_Profil,Delete',
                    PRF_IDF: profil.PRF_IDF
                }
            }, false);

            if (res.success) {
                blobticMessage.success(t('common.messages.success'), t('common.messages.deleted_success'));
                loadProfiles();
            } else {
                blobticMessage.error(t('common.error'), res.message || 'Erreur lors de la suppression.');
            }
        } catch (e: any) {
            blobticMessage.error(t('common.error'), e.message);
        }
    };

    // ── Form Handlers ──────────────────────────────────────────────────────────

    const handleInputChange = (field: keyof ProfilFormData, value: string | boolean) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handleClear = () => {
        setFormData(initialForm);
        setError(null);
    };

    const validate = () => {
        if (!formData.PRF_CODE || !formData.PRF_LIB) {
            return t('page.profiles.validation.required') || 'Code et Libellé sont obligatoires';
        }
        return null;
    };

    // ─── LIST VIEW ─────────────────────────────────────────────────────────────

    if (view === 'list') {
        return (
            <GenericPage
                title={t('page.profiles.title') || "Gestion des Profils"}
                icon={<BadgeIcon />}
                footerEnabled={false}
                viewMode={viewMode}
                onViewModeChange={setViewMode}
                onNew={openNew}
                newButtonLabel={t('page.profiles.add_new') || "Nouveau Profil"}
            >
                {listLoading ? (
                    <Box sx={{ p: 4, textAlign: 'center' }}>
                        <Typography color="text.secondary">Chargement en cours...</Typography>
                    </Box>
                ) : viewMode === 'grid' ? (
                    /* ── GRID VIEW ── */
                    <Grid container spacing={2.5}>
                        {profilesList.map(profil => {
                            const isAdmin = profil.PRF_ADMIN === true || String(profil.PRF_ADMIN) === 'true';

                            return (
                                <Grid item xs={12} sm={6} md={4} lg={3} key={profil.PRF_IDF || profil.PRF_CODE}>
                                    <Card
                                        variant="outlined"
                                        sx={{
                                            height: '100%',
                                            display: 'flex',
                                            flexDirection: 'column',
                                            borderRadius: 3,
                                            borderColor: isAdmin ? 'primary.main' : 'divider',
                                            bgcolor: 'background.paper',
                                            transition: 'all 0.2s',
                                            '&:hover': { boxShadow: 4, transform: 'translateY(-2px)' }
                                        }}
                                    >
                                        <CardContent sx={{ flexGrow: 1, p: 2.5 }}>
                                            {/* Header with Icon, Code, Title */}
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                                                <Avatar
                                                    variant="rounded"
                                                    sx={{
                                                        width: 46,
                                                        height: 46,
                                                        borderRadius: 2,
                                                        bgcolor: isAdmin ? 'primary.main' : 'action.selected',
                                                        color: isAdmin ? 'primary.contrastText' : 'text.primary'
                                                    }}
                                                >
                                                    {isAdmin ? <SecurityIcon /> : <BadgeIcon />}
                                                </Avatar>
                                                <Box sx={{ minWidth: 0, flexGrow: 1 }}>
                                                    <Typography variant="subtitle1" fontWeight="bold" noWrap>
                                                        {profil.PRF_LIB}
                                                    </Typography>
                                                    <Chip
                                                        label={profil.PRF_CODE}
                                                        size="small"
                                                        color="primary"
                                                        variant="outlined"
                                                        sx={{ height: 20, fontSize: '0.7rem', fontWeight: 600 }}
                                                    />
                                                </Box>
                                            </Box>

                                            <Divider sx={{ my: 1.5 }} />

                                            {/* Profile Metadata */}
                                            <Stack spacing={1.2} sx={{ mb: 2 }}>
                                                {profil.PRF_NIVEAU && (
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                        <LayersIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                                                        <Typography variant="body2" color="text.secondary">
                                                            {t('page.profiles.field.level') || 'Niveau'} : <strong>{profil.PRF_NIVEAU}</strong>
                                                        </Typography>
                                                    </Box>
                                                )}

                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    <ShieldOutlinedIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                                                    <Chip
                                                        label={isAdmin ? (t('page.profiles.status.admin') || 'Administrateur') : (t('page.profiles.status.standard') || 'Standard')}
                                                        size="small"
                                                        color={isAdmin ? 'error' : 'default'}
                                                        variant={isAdmin ? 'filled' : 'outlined'}
                                                        sx={{ height: 22, fontSize: '0.75rem', fontWeight: 600 }}
                                                    />
                                                </Box>
                                            </Stack>
                                        </CardContent>

                                        {/* Card Actions */}
                                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', px: 2, pb: 2, gap: 1 }}>
                                            <IconButton size="small" color="primary" onClick={() => openEdit(profil)}>
                                                <EditIcon fontSize="small" />
                                            </IconButton>
                                            <IconButton size="small" color="error" onClick={() => handleDelete(profil)}>
                                                <DeleteIcon fontSize="small" />
                                            </IconButton>
                                        </Box>
                                    </Card>
                                </Grid>
                            );
                        })}
                    </Grid>
                ) : (
                    /* ── TABLE VIEW ── */
                    <GenericTable
                        columns={[
                            { id: 'PRF_CODE', label: t('page.profiles.field.code') || 'Code', minWidth: 100 },
                            { id: 'PRF_LIB', label: t('page.profiles.field.label') || 'Libellé', minWidth: 200 },
                            { id: 'PRF_NIVEAU', label: t('page.profiles.field.level') || 'Niveau', minWidth: 100 },
                            {
                                id: 'PRF_ADMIN',
                                label: t('page.profiles.field.admin') || 'Administrateur',
                                minWidth: 140,
                                format: (val: any) => (val === true || String(val) === 'true') ? (
                                    <Chip
                                        label={t('page.profiles.status.admin') || 'Administrateur'}
                                        color="error"
                                        size="small"
                                        sx={{ fontWeight: 600 }}
                                    />
                                ) : (
                                    <Chip
                                        label={t('page.profiles.status.standard') || 'Standard'}
                                        size="small"
                                        variant="outlined"
                                    />
                                )
                            },
                            {
                                id: 'LAST_UPDT',
                                label: 'Dernière MAJ',
                                minWidth: 140,
                                format: (val: any) => val ? new Date(val).toLocaleDateString() : '-'
                            },
                            { id: 'USR_LIB', label: 'Modifié par', minWidth: 140 }
                        ]}
                        data={profilesList}
                        onEdit={(row) => openEdit(row as ProfilFormData)}
                        onDelete={(row) => handleDelete(row as ProfilFormData)}
                        filterColumns={['PRF_CODE', 'PRF_LIB']}
                        searchPlaceholder={t('page.profiles.search_placeholder') || "Rechercher un profil..."}
                        maxHeight="calc(100vh - 340px)"
                    />
                )}
            </GenericPage>
        );
    }

    // ─── FORM VIEW (New / Edit) ────────────────────────────────────────────────

    const isEditing = Boolean(formData.PRF_IDF);

    const footerInfo = {
        lastUpdate: formData.LAST_UPDT ? new Date(formData.LAST_UPDT).toLocaleString() : '-',
        userLib: formData.USR_LIB || '-'
    };

    return (
        <GenericPage<ProfilFormData>
            title={
                isEditing
                    ? (t('page.profiles.edit_title', formData.PRF_LIB || formData.PRF_CODE) || `Modifier le profil : ${formData.PRF_LIB || formData.PRF_CODE}`)
                    : (t('page.profiles.new_title') || "Nouveau Profil")
            }
            icon={<BadgeIcon />}
            footerEnabled={true}
            footerInfo={footerInfo}
            additionalActions={
                <Button
                    variant="outlined"
                    startIcon={<ArrowBackIcon />}
                    onClick={backToList}
                >
                    {t('page.profiles.back_to_list') || "Retour à la liste"}
                </Button>
            }
            crudConfig={{
                entityKey: 'Blobtic_Sys_Profil,Sys_Profil',
                primaryKey: 'PRF_IDF'
            }}
            data={formData}
            onDataChange={(data) => {
                setFormData(data);
                loadProfiles();
            }}
            onValidate={validate}
            onClear={handleClear}
        >
            <Box>
                {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

                <Grid container spacing={2.5}>
                    <Grid item xs={12} md={6}>
                        <TextField
                            fullWidth
                            label={t('page.profiles.field.code') || "Code Profil"}
                            value={formData.PRF_CODE}
                            onChange={(e) => handleInputChange('PRF_CODE', e.target.value)}
                            required
                        />
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <TextField
                            fullWidth
                            label={t('page.profiles.field.label') || "Libellé Profil"}
                            value={formData.PRF_LIB}
                            onChange={(e) => handleInputChange('PRF_LIB', e.target.value)}
                            required
                        />
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <TextField
                            fullWidth
                            label={t('page.profiles.field.level') || "Niveau"}
                            value={formData.PRF_NIVEAU || ''}
                            onChange={(e) => handleInputChange('PRF_NIVEAU', e.target.value)}
                        />
                    </Grid>

                    <Grid item xs={12} md={6} sx={{ display: 'flex', alignItems: 'center' }}>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={Boolean(formData.PRF_ADMIN)}
                                    onChange={(e) => handleInputChange('PRF_ADMIN', e.target.checked)}
                                    color="primary"
                                />
                            }
                            label={t('page.profiles.field.admin') || "Administrateur"}
                        />
                    </Grid>
                </Grid>
            </Box>
        </GenericPage>
    );
};

export default ProfilPage;
